/**
 * REEDOS Knowledge Base — full-page search results (`/kb/search?q=…`).
 *
 * The header's KBSearchBox handles instant top-5 dropdown; this page
 * shows the full paginated result set, grouped by article vs workflow.
 * Click-through logs an analytics event with `clicked_slug` so the
 * admin dashboard can rank result relevance.
 */
import { Link, useSearchParams } from "react-router-dom";
import { Search, ExternalLink } from "lucide-react";
import { useKbSearch, kbRecordSearch } from "@/lib/kb";
import { Badge } from "@/components/ui/badge";

function ResultRow({ hit, onClick }) {
    const kind = hit.kind;
    const to = `/kb/${kind === "workflow" ? "workflow" : "article"}/${hit.slug}`;
    return (
        <li>
            <Link
                to={to}
                onClick={onClick}
                className="block border border-border rounded-md p-3 hover:border-primary hover:bg-muted/40"
                data-testid={`kb-search-result-row-${hit.slug}`}
            >
                <div className="flex items-center justify-between gap-2">
                    <span className="font-medium truncate">{hit.title}</span>
                    <Badge variant="outline" className="text-[10px] uppercase">
                        {kind}
                    </Badge>
                </div>
                <div className="text-xs text-muted-foreground truncate mt-1">
                    {hit.product} · {hit.module || "—"} · {hit.slug}
                </div>
                {hit.tags?.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                        {hit.tags.slice(0, 6).map((t) => (
                            <span key={t} className="text-[10px] text-muted-foreground">
                                #{t}
                            </span>
                        ))}
                    </div>
                )}
            </Link>
        </li>
    );
}

export default function KBSearchPage() {
    const [params] = useSearchParams();
    const q = (params.get("q") || "").trim();
    const { data, loading, error } = useKbSearch(q, { limit: 50, minChars: 1, debounceMs: 100 });

    const results = data?.results || [];

    function onClick(slug) {
        return () => {
            kbRecordSearch({ query: q, results_count: results.length, clicked_slug: slug });
        };
    }

    return (
        <div className="max-w-3xl" data-testid="kb-search-page">
            <header className="mb-4">
                <h1 className="text-2xl font-semibold flex items-center gap-2">
                    <Search className="h-6 w-6 text-primary" />
                    Search
                </h1>
                {q ? (
                    <p className="text-muted-foreground text-sm mt-1">
                        {results.length} result{results.length === 1 ? "" : "s"} for “
                        <span className="text-foreground font-medium">{q}</span>”
                    </p>
                ) : (
                    <p className="text-muted-foreground text-sm mt-1">
                        Type a query in the header search box or add
                        <code className="mx-1 px-1 py-0.5 bg-muted rounded text-xs">?q=</code>
                        to the URL.
                    </p>
                )}
            </header>

            {loading && q && (
                <div className="text-sm text-muted-foreground" data-testid="kb-search-loading">
                    Searching…
                </div>
            )}
            {error && (
                <div className="text-sm text-destructive" data-testid="kb-search-error">
                    Search failed. Please try again.
                </div>
            )}
            {!loading && q && results.length === 0 && (
                <div
                    className="border border-dashed border-border rounded-md p-6 bg-muted/40 text-sm"
                    data-testid="kb-search-empty"
                >
                    No articles or workflows matched “{q}”.
                </div>
            )}

            {results.length > 0 && (
                <ul className="space-y-2">
                    {results.map((r) => (
                        <ResultRow
                            key={`${r.kind}-${r.slug}`}
                            hit={r}
                            onClick={onClick(r.slug)}
                        />
                    ))}
                </ul>
            )}
        </div>
    );
}
